// YNX — Cookie consent banner (RGPD compliant)
// ───────────────────────────────────────────────────────────────
// Shown ONCE on the public landing if the visitor hasn't decided yet.
// "Accept" enables analytics tracking; "Refuse" disables it permanently
// (stored in localStorage). Both decisions are remembered across visits.
// ───────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { getConsent, setConsent, track } from '../lib/tracker';

const TEXTS = {
  fr: {
    title:   'Cookies & confidentialité',
    body:    'Nous utilisons des cookies anonymes pour mesurer la fréquentation et améliorer YNX. Aucune donnée personnelle n\'est partagée avec des tiers.',
    accept:  'Accepter',
    decline: 'Refuser',
    learn:   'En savoir plus',
  },
  en: {
    title:   'Cookies & privacy',
    body:    'We use anonymous cookies to measure usage and improve YNX. No personal data is shared with third parties.',
    accept:  'Accept',
    decline: 'Decline',
    learn:   'Learn more',
  },
  es: {
    title:   'Cookies y privacidad',
    body:    'Usamos cookies anónimas para medir el uso y mejorar YNX. No compartimos datos personales con terceros.',
    accept:  'Aceptar',
    decline: 'Rechazar',
    learn:   'Más información',
  },
  de: {
    title:   'Cookies & Datenschutz',
    body:    'Wir verwenden anonyme Cookies, um die Nutzung zu messen und YNX zu verbessern. Keine personenbezogenen Daten werden weitergegeben.',
    accept:  'Akzeptieren',
    decline: 'Ablehnen',
    learn:   'Mehr erfahren',
  },
  it: {
    title:   'Cookie e privacy',
    body:    'Utilizziamo cookie anonimi per misurare l\'utilizzo e migliorare YNX. Nessun dato personale viene condiviso con terzi.',
    accept:  'Accetta',
    decline: 'Rifiuta',
    learn:   'Maggiori informazioni',
  },
};

export const ConsentBanner = ({ lang = 'fr' }) => {
  const [visible, setVisible] = useState(false);
  const t = TEXTS[lang] || TEXTS.fr;

  useEffect(() => {
    // Slight delay to avoid layout jank on first paint
    const id = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(id);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsent('accepted');
    setVisible(false);
    // The very first event we record is the consent itself
    track('consent_accepted');
  };
  const decline = () => {
    setConsent('declined');
    setVisible(false);
  };

  return (
    <div className='ynx-consent' role='dialog' aria-live='polite' data-testid='consent-banner'>
      <div className='ynx-consent-inner'>
        <div className='ynx-consent-icon'><Cookie size={20} /></div>
        <div className='ynx-consent-body'>
          <div className='ynx-consent-title'>{t.title}</div>
          <div className='ynx-consent-text'>{t.body}</div>
        </div>
        <div className='ynx-consent-actions'>
          <button
            type='button'
            className='ynx-consent-decline'
            onClick={decline}
            data-testid='consent-decline'
          >
            {t.decline}
          </button>
          <button
            type='button'
            className='ynx-consent-accept'
            onClick={accept}
            data-testid='consent-accept'
          >
            {t.accept}
          </button>
        </div>
        <button
          type='button'
          className='ynx-consent-close'
          onClick={decline}
          aria-label='Close'
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
